import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { cx, getDownloadURL } from '@/utils';
import Button from '@common/Button';
import SvgArrow from '@common/Icons/Arrow';
import SvgCalendarDay from '@common/Icons/CalendarDay';
import SvgShare from '@common/Icons/Share';
import { useHomeContext } from '@views/Home/HomeProvider/HomeContext';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import useEmblaCarousel from 'embla-carousel-react';
import ReactPlayer from 'react-player';

import { ROUTES_CLIENT } from '@/utils/constants';
import SvgCalendar from '@/components/common/Icons/Calendar';
import SvgMapMarker from '@/components/common/Icons/MapMarker';
import { getPlainTextFromEditorState } from '@/components/common/LexicalEditorComponent';
import {
  EventsItem,
  MultimediaTypes,
  PageBlockStyles,
} from '@/components/common/Providers/GraphqlProvider/generated';
import { Typography } from '@/components/ui/Typography';

import styles from './styles.module.css';

const EventsCard = ({ events }: { events: EventsItem[] }) => {
  const {
    home: { eventsBlock },
  } = useHomeContext();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 420px)': { slidesToScroll: 1 },
      '(min-width: 768px)': { slidesToScroll: 3 },
    },
  });

  const eventSorted = [...(events || [])].sort(
    (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div className="relative md:mt-3 md:border-t md:border-solid md:border-silver md:pt-3">
      {eventSorted.length ? (
        <div className="hidden md:block">
          <button
            className={'absolute inset-y-0 -left-10 z-10 hidden rotate-180 md:block'}
            onClick={() => emblaApi?.scrollPrev()}
          >
            <SvgArrow className="text-primary" />
          </button>
          <button
            className={'absolute inset-y-0 -right-10 z-10 hidden md:block'}
            onClick={() => emblaApi?.scrollNext()}
          >
            <SvgArrow className="text-primary" />
          </button>
        </div>
      ) : null}

      <div
        className="hidden overflow-hidden px-[1rem] pb-2 md:mx-0 md:block md:px-0"
        ref={emblaRef}
      >
        <div className={'mx-0 flex gap-[20px] pb-5  md:gap-[32px]'}>
          {eventSorted.map((event, key) => {
            return <EventCard key={key} event={event} style={eventsBlock?.pageBlockStyle} />;
          })}
        </div>
      </div>

      <div className="md:hidden">
        <EventCard event={eventSorted[0]} style={eventsBlock?.pageBlockStyle} />
        <div className="mt-4 flex items-center justify-center md:hidden">
          <Link
            className="rounded-3xl bg-primary px-3 py-2 text-sm text-white"
            href={`${ROUTES_CLIENT.EVENTS}`}
          >
            Ver más eventos
          </Link>
        </div>
      </div>
    </div>
  );
};

const EventCard = ({ event, style }: { event: EventsItem; style?: PageBlockStyles | null }) => {
  const { push } = useRouter();

  const goToEvent = (id: string) => {
    push(`/events/${id}`);
  };

  if (!event) return null;

  return (
    <div className="relative flex min-w-0 max-w-[556px] flex-[0_0_100%] flex-col items-start overflow-hidden rounded-[10px] border border-solid border-blueLight bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] md:flex-[0_0_31.733%]">
      <div className="relative flex h-40 w-full flex-col items-start gap-3 self-stretch md:h-48">
        {event.multimediaType === MultimediaTypes.Image && event.image ? (
          <Image
            src={getDownloadURL(event.image.id)}
            className={'rounded-t-[10px] object-cover'}
            alt={'event image'}
            fill
            sizes="(min-width: 768px) 31.733vw, (min-width: 420px) 50vw, 100vw"
            quality={70}
          />
        ) : event.multimediaType === MultimediaTypes.Video && event.linkItem ? (
          <div className="relative h-full w-full overflow-hidden rounded-t-[10px]">
            <ReactPlayer controls url={`${event.linkItem.url}`} muted width="100%" height="100%" />
          </div>
        ) : null}

        <div className="absolute left-[8px] top-[9px] hidden items-center gap-[12px] rounded-[20px] bg-primary p-[8px_12px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] md:flex">
          <SvgCalendarDay className="text-primary" />
          <Typography
            variant={'body2'}
            as={'span'}
            className="font-workSans text-[14px] font-medium not-italic leading-[21px] text-white"
          >
            {dayjs(event.startDate).format('LL')}
          </Typography>
        </div>

        <div className="absolute left-[8px] top-[9px] items-center gap-[12px] rounded-[20px] bg-light-green-50 px-2 py-1 text-sm font-bold text-greenDark md:hidden">
          {dayjs(event.endDate).isAfter(Date.now()) ? 'Activo' : 'Finalizado'}
        </div>
      </div>
      <div className="flex flex-[1_0_0] flex-col items-start justify-between gap-[10px] self-stretch p-4 md:p-[20px_16px_40px_16px]">
        <div className={'flex flex-col justify-start gap-1 md:gap-[10px]'}>
          <Typography
            variant={'h6'}
            className={
              'line-clamp-2 text-base font-semibold leading-tight text-primaryDark underline md:line-clamp-none md:text-base md:text-black md:no-underline'
            }
          >
            {event.eventItemTitle}
          </Typography>
          {style === PageBlockStyles.B ? (
            <div>{getPlainTextFromEditorState(event.summary || '')}</div>
          ) : null}
          <div className="flex items-center md:hidden">
            <SvgMapMarker className="text-primaryDark" width={12} height={12} />
            <span className="ml-2 line-clamp-1 font-workSans text-sm italic text-dimGray">
              {event.address}
            </span>
          </div>
        </div>

        <div className="hidden w-full md:flex">
          <Button
            onClick={() => goToEvent(event.id)}
            text={'Conoce más'}
            className="!w-full bg-primary text-white"
          />
        </div>
      </div>

      <div className="flex w-full justify-between bg-blueLight px-3 py-2 md:hidden">
        <EventDate label="Desde:" date={dayjs(event.startDate).format('D [de] MMM HH:mm')} />
        <EventDate label="Hasta:" date={dayjs(event.endDate).format('D [de] MMM HH:mm')} />
      </div>

      {event.hasShare && event.shareLink && (
        <button
          className="absolute -bottom-5 right-3 flex h-[42px] w-[42px] items-center justify-center rounded-[30px] bg-primaryDark"
          onClick={() => window.open(event.shareLink || '', '_blank')}
        >
          <SvgShare className="text-white" />
        </button>
      )}
    </div>
  );
};

const EventDate = ({ label, date }: { label: string; date: string }) => {
  return (
    <div className="flex items-center">
      <SvgCalendar className="text-primaryDark" />
      <div className="ml-2 flex flex-col">
        <span className="font-workSans text-sm text-primaryDark">{label}</span>
        <span className="font-workSans text-sm font-bold text-primaryDark">{date}</span>
      </div>
    </div>
  );
};

export default EventsCard;
